import React, { useEffect, useState } from 'react';
import { Heart, Plus, X, Save, Info, Activity, List as ListIcon } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import PatientDropdown from '../components/PatientDropdown';
import PatientVitalsTrends from '../components/PatientVitalsTrends';

const LOINC_CODES = {
  temperature: { code: '8310-5', name: 'Body temperature' },
  heartRate: { code: '8867-4', name: 'Heart rate' },
  bpSystolic: { code: '8480-6', name: 'Systolic blood pressure' },
  bpDiastolic: { code: '8462-4', name: 'Diastolic blood pressure' },
  respiratoryRate: { code: '9279-1', name: 'Respiratory rate' },
  oxygenSaturation: { code: '2708-6', name: 'Oxygen saturation' },
  weight: { code: '29463-7', name: 'Body weight' },
  height: { code: '8302-2', name: 'Body height' },
  bmi: { code: '39156-5', name: 'Body mass index' },
  painLevel: { code: '72514-3', name: 'Pain severity' },
  bloodGlucose: { code: '2345-7', name: 'Glucose [Mass/volume] in Blood' },
  headCircumference: { code: '9843-4', name: 'Head circumference' },
  waistCircumference: { code: '56086-2', name: 'Waist circumference' },
  meanArterialPressure: { code: '8478-0', name: 'Mean arterial pressure' },
};

function Vitals({ user }) {
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [vitals, setVitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table');   // 'table' | 'trends' — Task #39

  const [form, setForm] = useState({
    temperatureF: '',
    heartRate: '',
    bpSys: '',
    bpDia: '',
    respiratoryRate: '',
    oxygenSaturation: '',
    weightLbs: '',
    heightIn: '',
    painLevel: '',
    bloodGlucose: '',
    headCircumference: '',
    waistCircumference: '',
    visualAcuityLeft: '',
    visualAcuityRight: '',
    pulseOxMethod: '',
    position: '',
    observationMethod: '',
    recordingDevice: '',
  });

  useEffect(() => {
    async function load() {
      try {
        const data = await window.zignagenixAPI.getPatients();
        setPatients(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length > 0) {
          setSelectedPatientId(data[0].id);
          const vData = await window.zignagenixAPI.getVitals(data[0].id);
          setVitals(Array.isArray(vData) ? vData : []);
        }
      } catch (err) {
        console.error('Failed to load:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handlePatientChange(patientId) {
    setSelectedPatientId(patientId);
    try {
      const vData = await window.zignagenixAPI.getVitals(patientId);
      setVitals(Array.isArray(vData) ? vData : []);
    } catch (err) {
      console.error('Failed to load vitals:', err);
    }
  }

  const resetForm = () => {
    setForm({
      temperatureF: '',
      heartRate: '',
      bpSys: '',
      bpDia: '',
      respiratoryRate: '',
      oxygenSaturation: '',
      weightLbs: '',
      heightIn: '',
      painLevel: '',
      bloodGlucose: '',
      headCircumference: '',
      waistCircumference: '',
      visualAcuityLeft: '',
      visualAcuityRight: '',
      pulseOxMethod: '',
      position: '',
      observationMethod: '',
      recordingDevice: '',
    });
    setError(null);
  };

  const calculateBMI = (weightLbs, heightIn) => {
    const w = parseFloat(weightLbs);
    const h = parseFloat(heightIn);
    if (w > 0 && h > 0) {
      return ((w / (h * h)) * 703).toFixed(1);
    }
    return null;
  };

  const calculateMAP = (sys, dia) => {
    const s = parseFloat(sys);
    const d = parseFloat(dia);
    if (s > 0 && d > 0) {
      return (((s + 2 * d) / 3).toFixed(1));
    }
    return null;
  };

  const handleSubmit = async () => {
    try {
      const bmi = calculateBMI(form.weightLbs, form.heightIn);
      const map = calculateMAP(form.bpSys, form.bpDia);
      await window.zignagenixAPI.createVital({
        patientId: selectedPatientId,
        temperatureF: form.temperatureF ? parseFloat(form.temperatureF) : null,
        heartRate: form.heartRate ? parseInt(form.heartRate) : null,
        bpSys: form.bpSys ? parseInt(form.bpSys) : null,
        bpDia: form.bpDia ? parseInt(form.bpDia) : null,
        respiratoryRate: form.respiratoryRate ? parseInt(form.respiratoryRate) : null,
        oxygenSaturation: form.oxygenSaturation ? parseFloat(form.oxygenSaturation) : null,
        weightLbs: form.weightLbs ? parseFloat(form.weightLbs) : null,
        heightIn: form.heightIn ? parseFloat(form.heightIn) : null,
        bmi: bmi ? parseFloat(bmi) : null,
        painLevel: form.painLevel !== '' ? parseInt(form.painLevel) : null,
        bloodGlucose: form.bloodGlucose ? parseInt(form.bloodGlucose) : null,
        headCircumference: form.headCircumference ? parseFloat(form.headCircumference) : null,
        waistCircumference: form.waistCircumference ? parseFloat(form.waistCircumference) : null,
        visualAcuityLeft: form.visualAcuityLeft || null,
        visualAcuityRight: form.visualAcuityRight || null,
        meanArterialPressure: map ? parseFloat(map) : null,
        pulseOxMethod: form.pulseOxMethod || null,
        position: form.position || null,
        observationMethod: form.observationMethod || null,
        recordingDevice: form.recordingDevice || null,
      });
      const vData = await window.zignagenixAPI.getVitals(selectedPatientId);
      setVitals(Array.isArray(vData) ? vData : []);
      setShowForm(false);
      resetForm();
    } catch (err) {
      setError('Failed to save vitals');
      console.error(err);
    }
  };

  const hasCreatePermission = user?.permissions?.includes('vitals:create');

  if (loading) return <div className="page-loading">Loading vitals...</div>;

  return (
    <div className="page">
      <div className="page-header-row">
        <h1 className="page-title">Vital Signs</h1>
        <PrintButton pageTitle="Vital Signs" />
      </div>

      <div className="filter-bar">
        <label className="filter-label">Patient:</label>
        <PatientDropdown
          patients={patients}
          value={String(selectedPatientId)}
          onChange={val => handlePatientChange(Number(val))}
          placeholder="Select a patient"
          idPrefix="vitals"
        />

        {hasCreatePermission && selectedPatientId && (
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={18} /> Record Vitals
          </button>
        )}

        {selectedPatientId && (
          <div style={{ display: 'inline-flex', gap: 4, marginLeft: 8, padding: 2, background: '#f0f3f7', borderRadius: 4 }}>
            <button
              onClick={() => setViewMode('table')}
              style={{
                padding: '5px 10px', fontSize: 12, fontWeight: 600,
                background: viewMode === 'table' ? '#fff' : 'transparent',
                color: viewMode === 'table' ? '#1f2a3b' : '#5a6478',
                border: 'none', borderRadius: 3, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 4,
                boxShadow: viewMode === 'table' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            ><ListIcon size={13} /> Table</button>
            <button
              onClick={() => setViewMode('trends')}
              style={{
                padding: '5px 10px', fontSize: 12, fontWeight: 600,
                background: viewMode === 'trends' ? '#fff' : 'transparent',
                color: viewMode === 'trends' ? '#1f2a3b' : '#5a6478',
                border: 'none', borderRadius: 3, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 4,
                boxShadow: viewMode === 'trends' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            ><Activity size={13} /> Trends</button>
          </div>
        )}
      </div>

      {selectedPatientId && viewMode === 'trends' && (
        <PatientVitalsTrends patientId={selectedPatientId} user={user} />
      )}

      {showForm && (
        <div className="form-section">
          <h2 className="form-section-title">Record Vital Signs</h2>

          {error && (
            <div className="alert alert--error">{error}</div>
          )}

          <div className="patient-form-grid">
            {/* Row 1: Temperature, Heart Rate, Respiratory Rate, SpO2 */}
            <div className="form-group">
              <label className="form-label">
                Temperature (°F)
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.temperature.code}
                </span>
              </label>
              <input type="number" step="0.1" className="form-input" value={form.temperatureF}
                onChange={(e) => setForm(prev => ({ ...prev, temperatureF: e.target.value }))}
                placeholder="98.6" />
            </div>
            <div className="form-group">
              <label className="form-label">
                Heart Rate (bpm)
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.heartRate.code}
                </span>
              </label>
              <input type="number" className="form-input" value={form.heartRate}
                onChange={(e) => setForm(prev => ({ ...prev, heartRate: e.target.value }))}
                placeholder="72" />
            </div>
            <div className="form-group">
              <label className="form-label">
                Respiratory Rate (breaths/min)
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.respiratoryRate.code}
                </span>
              </label>
              <input type="number" className="form-input" value={form.respiratoryRate}
                onChange={(e) => setForm(prev => ({ ...prev, respiratoryRate: e.target.value }))}
                placeholder="16" />
            </div>
            <div className="form-group">
              <label className="form-label">
                SpO2 (%)
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.oxygenSaturation.code}
                </span>
              </label>
              <input type="number" step="0.1" className="form-input" value={form.oxygenSaturation}
                onChange={(e) => setForm(prev => ({ ...prev, oxygenSaturation: e.target.value }))}
                placeholder="98" />
            </div>

            {/* Row 2: BP Systolic, BP Diastolic, MAP (calculated), Pulse Ox Method */}
            <div className="form-group">
              <label className="form-label">
                Blood Pressure Systolic
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.bpSystolic.code}
                </span>
              </label>
              <input type="number" className="form-input" value={form.bpSys}
                onChange={(e) => setForm(prev => ({ ...prev, bpSys: e.target.value }))}
                placeholder="120" />
            </div>
            <div className="form-group">
              <label className="form-label">
                Blood Pressure Diastolic
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.bpDiastolic.code}
                </span>
              </label>
              <input type="number" className="form-input" value={form.bpDia}
                onChange={(e) => setForm(prev => ({ ...prev, bpDia: e.target.value }))}
                placeholder="80" />
            </div>
            <div className="form-group">
              <label className="form-label">
                MAP (Calculated)
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.meanArterialPressure.code}
                </span>
              </label>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#E0F7F4',
                borderRadius: '4px',
                color: '#2E8B98',
                fontWeight: 'bold',
                minHeight: '40px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px'
              }}>
                {calculateMAP(form.bpSys, form.bpDia) ? calculateMAP(form.bpSys, form.bpDia) : '—'}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Pulse Ox Method</label>
              <select className="form-input" value={form.pulseOxMethod}
                onChange={(e) => setForm(prev => ({ ...prev, pulseOxMethod: e.target.value }))}>
                <option value="">Select...</option>
                <option value="Room Air">Room Air</option>
                <option value="Nasal Cannula">Nasal Cannula</option>
                <option value="Mask">Mask</option>
                <option value="Ventilator">Ventilator</option>
              </select>
            </div>

            {/* Row 3: Weight, Height, BMI (calculated), Position */}
            <div className="form-group">
              <label className="form-label">
                Weight (lbs)
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.weight.code}
                </span>
              </label>
              <input type="number" step="0.1" className="form-input" value={form.weightLbs}
                onChange={(e) => setForm(prev => ({ ...prev, weightLbs: e.target.value }))}
                placeholder="165" />
            </div>
            <div className="form-group">
              <label className="form-label">
                Height (inches)
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.height.code}
                </span>
              </label>
              <input type="number" step="0.1" className="form-input" value={form.heightIn}
                onChange={(e) => setForm(prev => ({ ...prev, heightIn: e.target.value }))}
                placeholder="68" />
            </div>
            <div className="form-group">
              <label className="form-label">
                BMI (Calculated)
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.bmi.code}
                </span>
              </label>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#E0F7F4',
                borderRadius: '4px',
                color: '#2E8B98',
                fontWeight: 'bold',
                minHeight: '40px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px'
              }}>
                {calculateBMI(form.weightLbs, form.heightIn) ? calculateBMI(form.weightLbs, form.heightIn) : '—'}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Patient Position</label>
              <select className="form-input" value={form.position}
                onChange={(e) => setForm(prev => ({ ...prev, position: e.target.value }))}>
                <option value="">Select...</option>
                <option value="Sitting">Sitting</option>
                <option value="Standing">Standing</option>
                <option value="Supine">Supine</option>
                <option value="Prone">Prone</option>
              </select>
            </div>

            {/* Row 4: Blood Glucose, Pain Level, Waist Circumference, Head Circumference */}
            <div className="form-group">
              <label className="form-label">
                Blood Glucose (mg/dL)
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.bloodGlucose.code}
                </span>
              </label>
              <input type="number" className="form-input" value={form.bloodGlucose}
                onChange={(e) => setForm(prev => ({ ...prev, bloodGlucose: e.target.value }))}
                placeholder="100" />
            </div>
            <div className="form-group">
              <label className="form-label">
                Pain Level (0-10)
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.painLevel.code}
                </span>
              </label>
              <select className="form-input" value={form.painLevel}
                onChange={(e) => setForm(prev => ({ ...prev, painLevel: e.target.value }))}>
                <option value="">Select...</option>
                {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">
                Waist Circumference (inches)
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.waistCircumference.code}
                </span>
              </label>
              <input type="number" step="0.1" className="form-input" value={form.waistCircumference}
                onChange={(e) => setForm(prev => ({ ...prev, waistCircumference: e.target.value }))}
                placeholder="32" />
            </div>
            <div className="form-group">
              <label className="form-label">
                Head Circumference (cm)
                <span style={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  backgroundColor: '#E8F4F8',
                  color: '#2E8B98',
                  borderRadius: '4px',
                  fontWeight: 'normal',
                }}>
                  LOINC: {LOINC_CODES.headCircumference.code}
                </span>
              </label>
              <input type="number" step="0.1" className="form-input" value={form.headCircumference}
                onChange={(e) => setForm(prev => ({ ...prev, headCircumference: e.target.value }))}
                placeholder="35" />
            </div>

            {/* Row 5: Visual Acuity Left, Visual Acuity Right */}
            <div className="form-group">
              <label className="form-label">Visual Acuity Left Eye</label>
              <input type="text" className="form-input" value={form.visualAcuityLeft}
                onChange={(e) => setForm(prev => ({ ...prev, visualAcuityLeft: e.target.value }))}
                placeholder="20/20" />
            </div>
            <div className="form-group">
              <label className="form-label">Visual Acuity Right Eye</label>
              <input type="text" className="form-input" value={form.visualAcuityRight}
                onChange={(e) => setForm(prev => ({ ...prev, visualAcuityRight: e.target.value }))}
                placeholder="20/20" />
            </div>

            {/* Row 6: Observation Method, Recording Device */}
            <div className="form-group">
              <label className="form-label">Observation Method</label>
              <select className="form-input" value={form.observationMethod}
                onChange={(e) => setForm(prev => ({ ...prev, observationMethod: e.target.value }))}>
                <option value="">Select...</option>
                <option value="Measured">Measured</option>
                <option value="Estimated">Estimated</option>
                <option value="Stated by patient">Stated by patient</option>
                <option value="Calculated">Calculated</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Recording Device</label>
              <input type="text" className="form-input" value={form.recordingDevice}
                onChange={(e) => setForm(prev => ({ ...prev, recordingDevice: e.target.value }))}
                placeholder="e.g., Welch Allyn Connex, Omron BP7250" />
            </div>
          </div>

          <div className="form-actions">
            <button className="btn-primary" onClick={handleSubmit}>
              <Save size={18} /> Save Vitals
            </button>
            <button className="btn-ghost" onClick={() => { setShowForm(false); resetForm(); }}>
              <X size={18} /> Cancel
            </button>
          </div>
        </div>
      )}

      {viewMode === 'table' && (
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date/Time</th>
              <th>Temp (°F)</th>
              <th>HR</th>
              <th>BP</th>
              <th>MAP</th>
              <th>RR</th>
              <th>SpO2</th>
              <th>Weight (lbs)</th>
              <th>BMI</th>
              <th>Blood Glucose</th>
              <th>Pain</th>
            </tr>
          </thead>
          <tbody>
            {vitals.map((v) => (
              <tr key={v.id}>
                <td>{v.recorded_at}</td>
                <td className="mono">{v.temperature_f ?? '—'}</td>
                <td className="mono">{v.heart_rate ?? '—'}</td>
                <td className="mono">{v.blood_pressure_sys && v.blood_pressure_dia ? `${v.blood_pressure_sys}/${v.blood_pressure_dia}` : '—'}</td>
                <td className="mono">{v.mean_arterial_pressure ?? '—'}</td>
                <td className="mono">{v.respiratory_rate ?? '—'}</td>
                <td className="mono">{v.oxygen_saturation ? `${v.oxygen_saturation}%` : '—'}</td>
                <td className="mono">{v.weight_lbs ?? '—'}</td>
                <td className="mono">{v.bmi ?? '—'}</td>
                <td className="mono">{v.blood_glucose ?? '—'}</td>
                <td>{v.pain_level != null ? <span className={`pain-indicator pain-indicator--${v.pain_level > 5 ? 'high' : v.pain_level > 0 ? 'med' : 'low'}`}>{v.pain_level}/10</span> : '—'}</td>
              </tr>
            ))}
            {vitals.length === 0 && (
              <tr><td colSpan="11" className="empty-state">No vitals recorded</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      <div style={{
        marginTop: '32px',
        padding: '16px',
        backgroundColor: '#F9FAFB',
        borderRadius: '6px',
        borderLeft: '4px solid #2E8B98',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '12px',
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#2E8B98',
        }}>
          <Info size={16} style={{ marginRight: '8px' }} />
          LOINC Code Reference
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '12px',
          fontSize: '13px',
        }}>
          {Object.entries(LOINC_CODES).map(([key, value]) => (
            <div key={key} style={{
              padding: '8px',
              backgroundColor: '#FFFFFF',
              borderRadius: '4px',
              border: 'none', boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)',
            }}>
              <div style={{ fontWeight: '500', color: '#1F2937', marginBottom: '4px' }}>
                {value.name}
              </div>
              <div style={{ color: '#6B7280', fontSize: '12px' }}>
                Code: <span style={{ fontFamily: 'monospace', fontWeight: '500', color: '#2E8B98' }}>{value.code}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Vitals;
